/**
 * The project keymap (RFC 0266 §3d) — one document-level listener for the
 * whole shell.
 *
 * Deliberately NOT the legacy bindings. ⌘J was the browser's Downloads shortcut
 * and duplicated ⌥2, and ⌥-plus-digit is a character rather than a key (⌥1 types
 * ¡; ⌥3 types # on a UK layout, which is why the legacy set skipped it). One
 * binding per action, ⌘-plus-letter, editor conventions where they exist:
 *
 *   ⌘K  command bar          (the web's de-facto palette key)
 *   ⌘T  new space            (T for tab, in the client where the browser is not
 *                             in the way — see `newSpace` below)
 *   ⌘D  split right          (Ghostty / iTerm's split, same keys, same directions)
 *   ⌘⇧D split down
 *   ⌘⇧↵ maximize the focused pane, and back (iTerm's key; RFC 0305)
 *   ⌘B  files sidebar        (VS Code / Cursor's toggle-sidebar)
 *   ⌘.  the World thread     (RFC 0336 S7; no browser or OS default anywhere)
 *   ⌘⇧. the World thread, on a call (RFC 0336 S8)
 *   ⌘\  canvas side panel    (editor convention for a second pane)
 *   ⌘,  settings panel
 *   ⌘⌥+arrow  move the focused panel (the keyboard half of drag-and-drop)
 *   Esc close the open file  (last rung of the ladder below)
 *
 * Avoided because the browser owns them and never delivers them to the page:
 * ⌘W, ⌘N, ⌘L, ⌘[, ⌘], ⌘J.
 *
 * THE ESC LADDER. Esc must never destroy visible context, so the first match
 * wins and earlier rungs stop propagation before this listener runs:
 *   1. an open menu closes            (the menus' own handlers)
 *   2. a focused composer with text blurs, keeping its draft  (Composer)
 *   3. the open file closes           (here)
 * Esc on an empty focused composer therefore does nothing, so tapping it out of
 * habit is inert.
 *
 * Matching is on event.code, so a non-US layout fires the same bindings.
 *
 * ⌘/ is deliberately unbound (RFC 0348 S1): the legend it toggled moved to the
 * Shortcuts tab in settings, and the key is kept for something else.
 */

import { useEffect } from 'react'

export interface ProjectKeymap {
  toggleSidebar: () => void
  /**
   * ⌘J: the compact composer between always shown and shown at the bottom
   * edge only, paired with ⌘B for the sidebar as an editor pairs them.
   * Optional, so a host with no composer binds nothing.
   */
  toggleComposer?: () => void
  /** ⌘.: the World agent's thread, with no launcher rows (RFC 0336 S7). */
  openWorldThread: () => void
  toggleCanvasSide: () => void
  /** Called on Esc only when a file is open (rung 3 above). */
  closeFile: () => void
  hasOpenFile: boolean
  /** ⌘, — the settings panel. Optional so a host without one still binds. */
  toggleSettings?: () => void
  /**
   * ⌘P — the Packages modal (RFC 0284 S6l).
   *
   * A BINDING RATHER THAN A CONVENIENCE. Packages was reachable only from a
   * button in the sidebar, and a layout can empty the sidebar, so a person who
   * installed chrome and then emptied that slot could not reach the place that
   * uninstalls it. That is the same trap ⌘, exists for, and this sits beside
   * it: the two surfaces you must always be able to open.
   */
  openPackages?: () => void
  /**
   * ⌘⌥ + arrow — move the FOCUSED panel within the layout tree (layoutTree.ts).
   *
   * This is the keyboard half of drag-and-drop, and the modifier choice is forced:
   * ⌘ + arrow is text navigation (line start/end, document start/end) in every
   * text field on this platform, and the composer is a text field. ⌘⌥ + arrow is
   * unclaimed, and the arrows make the direction self-evident in a way a letter
   * cannot. Optional, so a host with a fixed layout binds nothing.
   */
  nudgePanel?: (edge: 'left' | 'right' | 'top' | 'bottom') => void
  /**
   * Esc with a session open — back to the session list.
   *
   * The panel header's back arrow already does this; this is the keyboard half,
   * and it makes Esc a LADDER rather than one action: close the open file
   * first, then leave the session, because that is the order they were entered
   * in. Optional, so a host without a session list binds nothing.
   */
  backToSessionList?: () => void
  /** True when a session is open, i.e. when there is something to go back FROM. */
  hasOpenSession?: boolean
  /**
   * ⌘1..9 / ⌘←→ — go to a space by POSITION (RFC 0281 §7).
   *
   * Positions, not names: inserting a space on the phone shifts what ⌘2 means
   * on the laptop, which is what a position is and what Mission Control and a
   * browser's tab strip both do. Binding digits to ids per device would hold
   * them still and make the same keystroke mean different spaces on two
   * machines, which is worse in the way that is harder to notice.
   *
   * Optional, so a host with no spaces binds nothing.
   */
  goToSpace?: (index: number) => void
  /** ⌘← / ⌘→ and ⌃⇧Tab / ⌃Tab — the space before or after this one. */
  cycleSpace?: (delta: -1 | 1) => void
  /**
   * ⌘K — the command bar.
   *
   * NOT ⌘T, which was the first instinct: T for "tab", answering the reflex of
   * reaching for a new tab in the desktop app and finding none. But Chrome
   * takes ⌘T for itself and never delivers it to the page, so the binding
   * worked in the desktop client and silently did nothing in the web one. A
   * shortcut that exists in one of two clients is worse than a different key.
   *
   * ⌘K is delivered in both, and is what every app with a palette already uses,
   * so it is also the one people try first. Bound even while a text field has
   * focus, unlike the panel toggles, since the whole point is to get somewhere
   * else from wherever you are.
   */
  openCommandBar?: () => void
  /**
   * ⌘⇧. — the World thread with a call on the World agent (RFC 0336 S8): ⌘.,
   * but spoken. Bound with text focused for the reason ⌘K is.
   */
  openWorldCall?: () => void
  /**
   * ⌘T — a new space, opened on an empty pane.
   *
   * The reflex the comment above ⌘K describes was right about the key and wrong
   * about the action: reaching for ⌘T means "give me a fresh surface", and in a
   * World that is a SPACE, not a palette. Chrome still keeps ⌘T for itself and
   * never delivers it, so this is a desktop-client binding that is simply
   * absent in the browser — acceptable here in a way it was not for the command
   * bar, because ⌘K is the only way to search and the space strip's `+` is
   * always on screen.
   *
   * Optional, so a host with no spaces binds nothing.
   */
  newSpace?: () => void
  /**
   * ⌘D / ⌘⇧D — split the focused pane, right and down.
   *
   * Ghostty's keys and Ghostty's directions, unchanged: ⌘D puts the new pane
   * beside this one, ⌘⇧D puts it underneath. Anyone who splits panes for a
   * living already has these in their hands, and a terminal is the thing this
   * shell is most often sitting next to, so being different would cost more
   * than it bought.
   *
   * ⌘D is the browser's bookmark shortcut, but unlike ⌘T or ⌘W it IS delivered
   * to the page and can be cancelled, so the binding behaves the same in the
   * desktop client and the web one. Optional, so a host with a fixed layout
   * binds nothing.
   */
  splitPane?: (direction: 'right' | 'down') => void
  /**
   * ⌘⇧Enter — the focused pane fills the space, and again puts the others back.
   *
   * iTerm2's chord for the same thing. Nothing here claims it: the composer's
   * Enter and Shift+Enter carry no platform modifier. Optional, so a host with
   * a fixed layout binds nothing.
   */
  toggleMaximized?: () => void
}

/** One row of the Shortcuts tab in settings (RFC 0348 3f). */
export interface KeyBinding {
  /** `event.code` as the listener below matches it; Esc and arrows by name. */
  code: string
  shift?: boolean
  alt?: boolean
  /** Ctrl rather than ⌘, for the one binding that must avoid ⌘Tab. */
  ctrl?: boolean
  /** How the key reads: "⌘⇧↵". */
  label: string
  /** What it does, in a person's words. `{world}` is the World agent's name. */
  action: string
}

/**
 * Every binding the listener handles, in the order the Shortcuts tab lists
 * them. A test presses each row and checks something fired, so a key added to
 * the listener without a row here, or a row for a key nothing handles, fails.
 */
export const KEYMAP: readonly KeyBinding[] = [
  { code: 'KeyK', label: '⌘K', action: 'Search and open anything' },
  { code: 'Period', label: '⌘.', action: 'Talk to {world}' },
  { code: 'Period', shift: true, label: '⌘⇧.', action: 'Call {world}' },
  { code: 'KeyB', label: '⌘B', action: 'Show or hide the files sidebar' },
  { code: 'KeyJ', label: '⌘J', action: 'Keep the composer out of the way' },
  { code: 'KeyD', label: '⌘D', action: 'Split the pane to the right' },
  { code: 'KeyD', shift: true, label: '⌘⇧D', action: 'Split the pane down' },
  {
    code: 'Enter',
    shift: true,
    label: '⌘⇧↵',
    action: 'Maximize the focused pane, and back',
  },
  {
    code: 'ArrowLeft',
    alt: true,
    label: '⌘⌥←→↑↓',
    action: 'Move the focused pane',
  },
  {
    code: 'Backslash',
    label: '⌘\\',
    action: 'Show or hide the canvas side panel',
  },
  { code: 'KeyT', label: '⌘T', action: 'New space' },
  { code: 'Digit1', label: '⌘1…9', action: 'Go to a space' },
  { code: 'ArrowRight', label: '⌘←→', action: 'The space before or after' },
  {
    code: 'Tab',
    ctrl: true,
    label: '⌃Tab  ⌃⇧Tab',
    action: 'The space before or after',
  },
  { code: 'KeyP', label: '⌘P', action: 'Packages' },
  { code: 'Comma', label: '⌘,', action: 'Settings' },
  {
    code: 'Escape',
    label: 'Esc',
    action: 'Close the open file, then leave the session',
  },
]

const ARROW_EDGES: Record<string, 'left' | 'right' | 'top' | 'bottom'> = {
  ArrowLeft: 'left',
  ArrowRight: 'right',
  ArrowUp: 'top',
  ArrowDown: 'bottom',
}

/**
 * An empty text field with the caret at the start, i.e. one where ← has nothing
 * to do. That is what makes a bare arrow safe to reuse for navigation: in a
 * field with any text, or with the caret anywhere but 0, the key still means
 * "move left" and is left alone.
 */
const isAtStartOfEmptyField = (target: EventTarget | null): boolean => {
  const el = target as HTMLTextAreaElement | HTMLInputElement | null
  if (!el || (el.tagName !== 'TEXTAREA' && el.tagName !== 'INPUT')) return false
  if (el.value.length > 0) return false
  return (el.selectionStart ?? 0) === 0 && (el.selectionEnd ?? 0) === 0
}

const isTextEntry = (target: EventTarget | null): boolean => {
  const el = target as HTMLElement | null
  if (!el) return false
  const tag = el.tagName
  return tag === 'INPUT' || tag === 'TEXTAREA' || el.isContentEditable
}

export function useProjectKeymap(keymap: ProjectKeymap) {
  const {
    openCommandBar,
    openWorldCall,
    toggleSidebar,
    toggleComposer,
    openWorldThread,
    toggleCanvasSide,
    closeFile,
    hasOpenFile,
    toggleSettings,
    openPackages,
    nudgePanel,
    backToSessionList,
    hasOpenSession,
    splitPane,
    goToSpace,
    cycleSpace,
    newSpace,
    toggleMaximized,
  } = keymap

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      // Esc reaches here only if no earlier rung consumed it. A bare key, so it
      // must never fire while the user is typing into something else.
      if (event.code === 'Escape') {
        // Never while typing: Esc in the composer belongs to the composer.
        if (isTextEntry(event.target)) return
        // The ladder. A file is the innermost thing you opened, so it closes
        // first; a second Esc then leaves the session. Doing both at once would
        // make one keypress undo two decisions.
        if (hasOpenFile) {
          event.preventDefault()
          closeFile()
          return
        }
        if (hasOpenSession && backToSessionList) {
          event.preventDefault()
          backToSessionList()
        }
        return
      }

      // ← out of an EMPTY composer goes back to the session list.
      //
      // A bare arrow is only safe because of the emptiness check: with a draft,
      // or with the caret past the start, ← still means "move left" and this
      // does not fire. Opening a session puts the caret in the composer, so this
      // is the reverse of the gesture that got you here, and it costs nothing to
      // discover: press it once with an empty box and you are back.
      if (event.code === 'ArrowLeft' && !event.metaKey && !event.ctrlKey) {
        if (event.altKey || event.shiftKey) return
        const inField = isTextEntry(event.target)
        if (inField && !isAtStartOfEmptyField(event.target)) return
        if (!hasOpenSession || !backToSessionList) return
        event.preventDefault()
        backToSessionList()
        return
      }

      // Everything else carries the platform modifier, which makes it untypable
      // and therefore safe to honour even with a text field focused.
      const mod = event.metaKey || event.ctrlKey
      if (!mod) return

      // ⌘D / ⌘⇧D — the one binding here that WANTS shift, so it is matched
      // before the shift-free rules below turn it away.
      if (event.code === 'KeyD' && !event.altKey) {
        if (!splitPane) return
        event.preventDefault()
        splitPane(event.shiftKey ? 'down' : 'right')
        return
      }

      // ⌘⇧Enter wants shift too, so it is matched here for the same reason.
      if (
        (event.code === 'Enter' || event.code === 'NumpadEnter') &&
        event.shiftKey &&
        !event.altKey
      ) {
        if (!toggleMaximized) return
        event.preventDefault()
        toggleMaximized()
        return
      }

      // ⌃Tab / ⌃⇧Tab — the space either side, as Chrome walks its tabs. Ctrl
      // on every platform, not ⌘: ⌘Tab is the OS app switcher. Wants shift, so
      // it is matched before the shift-free rules.
      if (event.code === 'Tab' && event.ctrlKey && !event.metaKey) {
        if (event.altKey || !cycleSpace) return
        event.preventDefault()
        cycleSpace(event.shiftKey ? -1 : 1)
        return
      }

      if (event.code === 'Period' && event.shiftKey && !event.altKey) {
        if (!openWorldCall) return
        event.preventDefault()
        openWorldCall()
        return
      }

      if (event.shiftKey) return

      // ⌘1..9 — the Nth space. Before the alt branch, and alt-free: ⌘⌥1 is not
      // ours and should fall through rather than being swallowed.
      if (!event.altKey && goToSpace && /^Digit[1-9]$/.test(event.code)) {
        event.preventDefault()
        goToSpace(Number(event.code.slice(5)) - 1)
        return
      }

      // ⌘← / ⌘→ — the space either side. Checked before the panel nudge, which
      // needs alt, so the two never contend for the same chord.
      if (
        !event.altKey &&
        cycleSpace &&
        (event.code === 'ArrowLeft' || event.code === 'ArrowRight')
      ) {
        event.preventDefault()
        cycleSpace(event.code === 'ArrowRight' ? 1 : -1)
        return
      }

      // ⌘⌥ + arrow moves the focused panel. Checked BEFORE the alt-free bindings,
      // which is why alt is no longer rejected outright above.
      if (event.altKey) {
        const edge = ARROW_EDGES[event.code]
        if (edge && nudgePanel) {
          event.preventDefault()
          nudgePanel(edge)
        }
        return
      }

      switch (event.code) {
        case 'KeyT':
          if (!newSpace) break
          event.preventDefault()
          newSpace()
          break
        case 'KeyK':
          if (!openCommandBar) break
          event.preventDefault()
          openCommandBar()
          break
        case 'KeyB':
          event.preventDefault()
          toggleSidebar()
          break
        case 'KeyJ':
          if (!toggleComposer) break
          event.preventDefault()
          toggleComposer()
          break
        case 'Period':
          event.preventDefault()
          openWorldThread()
          break
        case 'Backslash':
          event.preventDefault()
          toggleCanvasSide()
          break
        case 'KeyP':
          // ⌘P is the browser's print, and like ⌘D's bookmark it IS delivered
          // to the page, so preventDefault is enough to claim it.
          if (!openPackages) break
          event.preventDefault()
          openPackages()
          break
        case 'Comma':
          // ⌘, is the platform's settings shortcut everywhere; a project app
          // that used anything else would be being different for no reason.
          if (!toggleSettings) break
          event.preventDefault()
          toggleSettings()
          break
        default:
          break
      }
    }

    document.addEventListener('keydown', onKeyDown)
    return () => document.removeEventListener('keydown', onKeyDown)
  }, [
    toggleSidebar,
    toggleComposer,
    openWorldThread,
    toggleCanvasSide,
    closeFile,
    hasOpenFile,
    toggleSettings,
    openPackages,
    nudgePanel,
    openCommandBar,
    openWorldCall,
    backToSessionList,
    hasOpenSession,
    splitPane,
    goToSpace,
    cycleSpace,
    newSpace,
    toggleMaximized,
  ])
}
